import type { ReactNode } from "react";
import { IconeAlerta, IconeCarteira, IconeCheck, IconeInfo, IconeSeta } from "../icones";
import { LinkInterno } from "../navegacao";

export function Aviso({ tipo = "info", children }: { tipo?: "info" | "sucesso" | "alerta" | "erro"; children: ReactNode }) {
  const Icone = tipo === "sucesso" ? IconeCheck : tipo === "info" ? IconeInfo : IconeAlerta;
  return (
    <div className={`mtc-aviso mtc-aviso--${tipo}`} role={tipo === "erro" ? "alert" : "status"}>
      <Icone tamanho={16} />
      <div>{children}</div>
    </div>
  );
}

export type Tom = "neutro" | "acento" | "sucesso" | "alerta" | "erro" | "info";

/** Etiqueta curta (papel, status). `tom` pinta pelo estado; `acento` e o atalho antigo pra tom="acento". */
export function Selo({ acento, tom, titulo, destaque, icone, children }: { acento?: boolean; tom?: Tom; /** A frase inteira, pra quem passa o mouse e pro leitor de tela (o selo e abreviado). */ titulo?: string; /** Maior e mais forte: o que ninguem pode deixar de ver (ex.: "Sem assinatura"). */ destaque?: boolean; icone?: ReactNode; children: ReactNode }) {
  const t = tom ?? (acento ? "acento" : "neutro");
  return <span className={`mtc-selo${t !== "neutro" ? ` mtc-selo--${t}` : ""}${destaque ? " mtc-selo--destaque" : ""}`} title={titulo} aria-label={titulo}>{icone}{children}</span>;
}

/**
 * Checkout que so cobra (spec 036, decisao 8): no lugar do nome do plano, em todo lugar onde o checkout ou a venda
 * aparece (painel e pagina de pagamento). Grande e a vista, pra ninguem procurar plano, conta ou acesso que nao existe.
 */
export function SeloSemAssinatura() {
  return <Selo destaque icone={<IconeCarteira tamanho={16} />}>Sem assinatura</Selo>;
}

export function Cartao({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={`mtc-cartao${className ? ` ${className}` : ""}`}>{children}</section>;
}

export function Etapas({ etapas, atual }: { etapas: string[]; atual: number }) {
  return (
    <ol className="mtc-etapas" aria-label={`Etapa ${atual + 1} de ${etapas.length}`}>
      {etapas.map((e, i) => (
        <li key={e} data-estado={i < atual ? "feita" : i === atual ? "atual" : "futura"} aria-current={i === atual ? "step" : undefined}>{e}</li>
      ))}
    </ol>
  );
}

export function Separador({ children }: { children: ReactNode }) {
  return <div className="mtc-separador">{children}</div>;
}

export type ItemDeAjuda = { titulo: string; descricao: string; href: string };
export function ListaDeAjuda({ itens }: { itens: ItemDeAjuda[] }) {
  return (
    <ul className="mtc-ajuda">
      {itens.map((i) => (
        <li key={i.href}>
          <LinkInterno href={i.href}>
            <span>
              <span className="mtc-ajuda__titulo">{i.titulo}</span>
              <span className="mtc-ajuda__descricao">{i.descricao}</span>
            </span>
            <IconeSeta className="mtc-ajuda__seta" tamanho={16} />
          </LinkInterno>
        </li>
      ))}
    </ul>
  );
}

export function Segmentado<T extends string>({ rotulo, nome, opcoes, valor, aoMudar, quebra }: { rotulo: string; nome: string; opcoes: { valor: T; rotulo: string }[]; valor: T; aoMudar: (v: T) => void; /** Muitas opcoes (o periodo do dashboard): no celular vira grade de 3 colunas em vez de espremer a pilula. */ quebra?: boolean }) {
  return (
    <div className="mtc-campo" role="radiogroup" aria-label={rotulo}>
      <span className="mtc-campo__rotulo">{rotulo}</span>
      <div className={`mtc-segmentado${quebra ? " mtc-segmentado--quebra" : ""}`}>
        {opcoes.map((o) => (
          <label key={o.valor} className="mtc-segmentado__opcao">
            <input type="radio" name={nome} value={o.valor} checked={valor === o.valor} onChange={() => aoMudar(o.valor)} />
            <span>{o.rotulo}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Barra de progresso (Academy, spec 011): quanto da aula ou do modulo ja foi visto. `rotulo` vira o texto acessivel;
 * o numero fica visivel so quando `mostrarNumero`.
 */
export function BarraDeProgresso({ percentual, rotulo, mostrarNumero }: { percentual: number; rotulo: string; mostrarNumero?: boolean }) {
  const p = Math.max(0, Math.min(100, Math.round(percentual)));
  return (
    <div className="mtc-progresso" role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} aria-label={rotulo}>
      <div className="mtc-progresso__trilho"><div className="mtc-progresso__barra" style={{ width: `${p}%` }} /></div>
      {mostrarNumero && <span className="mtc-progresso__numero mtc-mono">{p}%</span>}
    </div>
  );
}

/** Tela inteira por padrao; `compacto` cabe dentro de uma pagina ou de um cartao. */
export function Carregando({ texto = "Carregando", compacto }: { texto?: string; compacto?: boolean }) {
  return (
    <div className={`mtc-carregando${compacto ? " mtc-carregando--compacto" : ""}`} role="status" aria-live="polite">
      <span><span className="mtc-giro" aria-hidden />{texto}</span>
    </div>
  );
}
